import React from "react";
import toast from 'react-hot-toast';
import { getScoreColorByThreshold, getPassedColor } from "../../utils/vacancies/formatting";
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from "../../hooks/useBrandingColors";
import { apiService } from "../../services/apiService";
import { ShareForwardIcon } from '../icons/ShareForwardIcon';
import { ArrowLeftIcon } from '../icons/ArrowLeftIcon';
import { FilePdf2LineIconV7 } from '../ui/figma-icons-v7';
import { ReactComponent as MessageCircleIcon } from '../../assets/icons/message-circle.svg';
import { useNavigate } from 'react-router-dom';

const SECTION_HEADERS = [
  'ИТОГОВАЯ ОЦЕНКА',
  'ОБОСНОВАНИЕ ОЦЕНКИ',
  'ДЕТАЛЬНАЯ ОЦЕНКА ПО КРИТЕРИЯМ',
  'СИЛЬНЫЕ СТОРОНЫ',
  'РЕКОМЕНДАЦИИ ДЛЯ РАЗВИТИЯ',
  'ОБЛАСТИ ДЛЯ УЛУЧШЕНИЯ',
];

function normalizeFeedback(feedback: string): string {
  let result = feedback;

  // Убираем дублирование заголовка "ОБОСНОВАНИЕ ОЦЕНКИ:" если встречается дважды подряд
  result = result.replace(/(ОБОСНОВАНИЕ ОЦЕНКИ:\s*\n?)(ОБОСНОВАНИЕ ОЦЕНКИ:)/g, '$1');

  // Перед каждым заголовком раздела добавляем двойной перенос строки, если его нет
  for (const header of SECTION_HEADERS) {
    result = result.replace(new RegExp(`([^\\n])(${header}:)`, 'g'), '$1\n\n$2');
  }

  // Перед каждым буллетом • добавляем перенос строки, если его нет
  result = result.replace(/([^\n])(•)/g, '$1\n$2');

  return result.trim();
}

export interface CandidateAnswersPanelProps {
  selectedCandidate: any;
  selectedPositionId: number | null;
  selectedPositionTitle?: string;
  selectedPosition?: any; // Добавляем данные вакансии для доступа к minScore
  interviewLoading: boolean;
  interviewError: string | null;
  interviewDetails: any;
  expandedAnswers: { [key: number]: boolean };
  formatDate: (isoDate: string) => string;
  onRetry: () => void;
  onCreateInterview?: () => void;
  onToggleAnswer: (index: number) => void;
  onBack?: () => void;
}

export const CandidateAnswersPanel: React.FC<CandidateAnswersPanelProps> = ({
  selectedCandidate,
  selectedPositionId,
  selectedPositionTitle,
  selectedPosition,
  interviewLoading,
  interviewError,
  interviewDetails,
  expandedAnswers,
  formatDate,
  onRetry,
  onCreateInterview,
  onToggleAnswer,
  onBack,
}) => {
  // Функция для определения цвета оценки относительно минимального балла
  const getScoreColor = (score: number | null | undefined) => {
    const minScore = selectedPosition?.minScore || 0;
    return getScoreColorByThreshold(score as any, minScore);
  };

  // Функция для определения цвета статуса прохождения
  const getPassedColorLocal = (isPassed: boolean) => {
    return getPassedColor(isPassed);
  };
  const navigate = useNavigate();
  const colors = useBrandingColors();
  const [isPdfLoading, setIsPdfLoading] = React.useState(false);
  const [totalQuestionsFromApi, setTotalQuestionsFromApi] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!selectedPositionId || totalQuestionsFromApi !== null) {
      return;
    }

    if (selectedPosition?.questionsCount) {
      setTotalQuestionsFromApi(selectedPosition.questionsCount);
      return;
    }

    apiService
      .getPositionQuestionsWithSettings(selectedPositionId)
      .then((data: any) => {
        const questions = data?.questions || [];
        setTotalQuestionsFromApi(questions.length || 0);
      })
      .catch((error) => {
        console.error('Failed to load position questions for total count', error);
      });
  }, [selectedPositionId, selectedPosition, totalQuestionsFromApi]);
  // Loading state
  if (interviewLoading) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="bg-[#f5f6f1] relative rounded-[24px] shrink-0 w-full">
          <div className="flex items-center justify-center p-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Загрузка деталей интервью...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (interviewError) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="bg-[#f5f6f1] relative rounded-[24px] shrink-0 w-full">
          <div className="flex items-center justify-center p-12">
            <div className="text-center">
              <div className="text-red-600 text-xl mb-4">⚠️</div>
              <p className="text-gray-600 mb-4">{interviewError}</p>
              <button
                onClick={onRetry}
                className="text-white px-4 py-2 rounded-lg"
                style={getButtonInlineStyles(colors, 'primary')}
                onMouseEnter={(e) => { Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary')); }}
                onMouseLeave={(e) => { Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary')); }}
              >
                Попробовать снова
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const candidateName = (() => {
    console.log('🔍 CandidateAnswersPanel - interviewDetails:', interviewDetails);
    console.log('🔍 CandidateAnswersPanel - selectedCandidate:', selectedCandidate);
    
    // Сначала пытаемся получить имя из interviewDetails.candidate
    if (interviewDetails?.candidate?.firstName || interviewDetails?.candidate?.lastName) {
      const firstName = interviewDetails.candidate.firstName || '';
      const lastName = interviewDetails.candidate.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim() || 'Кандидат';
      console.log('🔍 Using interviewDetails.candidate name:', fullName);
      return fullName;
    }
    
    // Затем из selectedCandidate
    if (selectedCandidate?.name) {
      console.log('🔍 Using selectedCandidate.name:', selectedCandidate.name);
      return selectedCandidate.name;
    }
    
    // Фоллбэк
    console.log('🔍 Using fallback name: Кандидат');
    return "Кандидат";
  })();

  // Безопасные извлечения данных с фоллбэками из источника клика
  const safeEmail = (
    interviewDetails?.candidate?.email ||
    interviewDetails?.interview?.candidateEmail ||
    interviewDetails?.interview?.email ||
    interviewDetails?.candidateEmail ||
    interviewDetails?.email ||
    selectedCandidate?.email ||
    selectedCandidate?.candidateEmail ||
    selectedCandidate?.candidate?.email ||
    ''
  );

  const safeCreatedAt = (
    interviewDetails?.interview?.createdAt ||
    interviewDetails?.createdAt ||
    selectedCandidate?.createdAt ||
    null
  );

  const safeFinishedAt = (
    interviewDetails?.interview?.finishedAt ||
    interviewDetails?.finishedAt ||
    selectedCandidate?.finishedAt ||
    null
  );

  const answeredQuestions = interviewDetails?.answers?.length || 0;
  const totalQuestions =
    totalQuestionsFromApi != null
      ? totalQuestionsFromApi
      : ((interviewDetails && (interviewDetails as any).totalQuestions) ||
        selectedPosition?.questionsCount ||
        answeredQuestions);

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Карточка 1: Имя кандидата и действия */}
      <div className="bg-[#f5f6f1] relative rounded-[24px] shrink-0 w-full">
        <div className="relative size-full">
          <div className="box-border content-stretch flex flex-col gap-6 items-start justify-start p-[24px] relative w-full">
            {/* Header with Actions */}
            <div className="box-border content-stretch flex flex-row items-center justify-between p-0 relative shrink-0 w-full">
              <div className="flex flex-row items-center gap-4">
                {/* Back Link */}
                <button 
                  onClick={() => onBack ? onBack() : navigate(-1)}
                  className="flex flex-row items-center gap-2 cursor-pointer hover:opacity-70 transition-opacity"
                >
                  <ArrowLeftIcon color={colors.primary} size={20} />
                  <div className="font-['Inter',_sans-serif] font-medium text-[16px]" style={{ color: colors.primary, letterSpacing: '-0.011em', lineHeight: '1.5em' }}>
                    Назад
                  </div>
                </button>

                <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[32px] text-center text-nowrap">
                  <p className="block leading-[40px] whitespace-pre">{candidateName}</p>
                </div>
              </div>

              <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start p-0 relative shrink-0">
                {/* Download PDF */}
                <button
                  onClick={async () => {
                    if (isPdfLoading) return;
                    
                    try {
                      setIsPdfLoading(true);
                      const interviewId = selectedCandidate?.interviewId || selectedCandidate?.id;
                      
                      if (!interviewId) {
                        console.error('ID интервью не найден');
                        console.error('selectedCandidate:', selectedCandidate);
                        console.error('interviewDetails:', interviewDetails);
                        alert('Ошибка: ID интервью не найден');
                        return;
                      }

                      console.log('Скачивание PDF отчета для интервью:', interviewId);
                      
                      await apiService.downloadInterviewResultPDF(
                        interviewId, 
                        `candidate-results-${candidateName}-${new Date().toISOString().split('T')[0]}.pdf`
                      );
                      
                    } catch (error) {
                      console.error('Ошибка при скачивании PDF:', error);
                      alert('Ошибка при скачивании PDF отчета. Попробуйте еще раз.');
                    } finally {
                      setIsPdfLoading(false);
                    }
                  }}
                  disabled={isPdfLoading}
                  className="bg-[#ffffff] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip p-2 relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors cursor-pointer border disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ borderColor: colors.primary }}
                >
                  <div className="font-['Inter',_sans-serif] font-medium text-[16px] text-center whitespace-nowrap" style={{ color: colors.primary, letterSpacing: '-0.011em', lineHeight: '1.5em' }}>
                    {isPdfLoading ? "Загрузка..." : "Скачать PDF"}
                  </div>
                  <FilePdf2LineIconV7 size={20} color={colors.primary} />
                </button>

                {/* Share Button */}
                <button
                  onClick={() => {
                    const shareData = {
                      title: `Результаты кандидата: ${candidateName}`,
                      text: `Позиция: ${interviewDetails?.position?.title || selectedPositionTitle || "не выбрана"}, Оценка: ${
                        interviewDetails?.interview?.aiScore || "—"
                      }/100, Пройдено: ${interviewDetails?.interview?.result === "SUCCESSFUL" ? "Да" : "Нет"}`,
                      url: window.location.href,
                    };

                    if (navigator.share) {
                      navigator.share(shareData);
                    } else {
                      navigator.clipboard
                        .writeText(`${shareData.title}\n${shareData.text}\n${shareData.url}`)
                        .catch(() => {});
                      alert("Информация скопирована в буфер обмена");
                    }

                    console.log("Share functionality executed:", shareData);
                  }}
                  className="box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip p-2 relative rounded-2xl shrink-0 transition-colors cursor-pointer"
                  style={getButtonInlineStyles(colors, 'primary')}
                  onMouseEnter={(e) => { Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary')); }}
                  onMouseLeave={(e) => { Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary')); }}
                >
                  <ShareForwardIcon color="#FFFFFF" size={20} />
                  <div className="font-['Inter',_sans-serif] font-medium text-[#ffffff] text-[16px] text-center whitespace-nowrap" style={{ letterSpacing: '-0.011em', lineHeight: '1.5em' }}>
                    Поделиться
                  </div>
                </button>
              </div>
            </div>

            {/* Candidate Stats */}
            <div className="bg-[#ffffff] relative rounded-2xl shrink-0 w-full">
              <div className="relative size-full">
                <div className="box-border content-stretch flex flex-col gap-6 items-start justify-start p-[24px] relative w-full">
                  <div className="box-border content-stretch flex flex-row gap-16 items-center justify-start p-0 relative shrink-0 w-full">
                    {/* Rating */}
                    <div className="box-border content-stretch flex flex-col gap-2 items-center justify-center leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-center text-nowrap tracking-[-0.176px]">
                      <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal relative shrink-0 text-[#525866]">
                        <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">Оценка</p>
                      </div>
                      <div className={`css-o3fd0r font-['Inter:Semi_Bold',_sans-serif] font-semibold relative shrink-0 ${getScoreColor(interviewDetails?.overallScore)}`}>
                        <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">{interviewDetails?.overallScore || "—"}</p>
                      </div>
                    </div>

                    {/* Passed */}
                    <div className="box-border content-stretch flex flex-col gap-2 items-center justify-center leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-center text-nowrap tracking-[-0.176px]">
                      <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal relative shrink-0 text-[#525866]">
                        <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">Пройдено</p>
                      </div>
                      <div className={`css-o3fd0r font-['Inter:Semi_Bold',_sans-serif] font-semibold relative shrink-0 ${getPassedColorLocal(interviewDetails?.overallScore >= (selectedPosition?.minScore || 0))}`}>
                        <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">{interviewDetails?.overallScore >= (selectedPosition?.minScore || 0) ? "Да" : "Нет"}</p>
                      </div>
                    </div>

                    {/* Questions Answered */}
                    <div className="box-border content-stretch flex flex-col gap-2 items-center justify_center leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-center text-nowrap tracking-[-0.176px]">
                      <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal relative shrink-0 text-[#525866]">
                        <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">Вопросов отвечено</p>
                      </div>
                      <div className="css-8vheua font-['Inter:Semi_Bold',_sans-serif] font-semibold relative shrink-0 text-[#000000]">
                        <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">{answeredQuestions}/{totalQuestions}</p>
                      </div>
                    </div>

                    {/* Contact Info: email and phone */}
                    <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 min-w-[260px]">
                      <div className="box-border content-stretch flex flex-row items-center justify-between leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-left text-nowrap tracking-[-0.176px] w-full">
                        <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal relative shrink-0 text-[#525866]">
                          <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">Email</p>
                        </div>
                        <div className="css-8vheua font-['Inter:Medium',_sans-serif] font-medium relative shrink-0 text-[#000000]">
                          <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                            {safeEmail || '—'}
                          </p>
                        </div>
                      </div>

                      <div className="box-border content-stretch flex flex-row items-center justify-between leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-left text-nowrap tracking-[-0.176px] w-full">
                        <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal relative shrink-0 text-[#525866]">
                        
                        </div>
                        <div className="css-8vheua font-['Inter:Medium',_sans-serif] font-medium relative shrink-0 text-[#000000]">
                          <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">-</p>
                        </div>
                      </div>
                    </div>

                    {/* Timestamps */}
                    <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 min-w-[200px]">
                      <div className="box-border content-stretch flex flex-row items-center justify-between leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-left text-nowrap tracking-[-0.176px] w-full">
                        <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal relative shrink-0 text-[#525866]">
                          <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">Создано</p>
                        </div>
                        <div className="css-8vheua font-['Inter:Medium',_sans-serif] font-medium relative shrink-0 text-[#000000]">
                          <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">{safeCreatedAt ? formatDate(safeCreatedAt as any) : '—'}</p>
                        </div>
                      </div>

                      <div className="box-border content-stretch flex flex-row items-center justify-between leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-left text-nowrap tracking-[-0.176px] w-full">
                        <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal relative shrink-0 text-[#525866]">
                          <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">Завершено</p>
                        </div>
                        <div className="css-8vheua font-['Inter:Medium',_sans-serif] font-medium relative shrink-0 text-[#000000]">
                          <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">{safeFinishedAt ? formatDate(safeFinishedAt as any) : '—'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Карточка 2: Краткие итоги */}
      <div className="bg-[#f5f6f1] relative rounded-[24px] shrink-0 w-full">
        <div className="relative size-full">
          <div className="box-border content-stretch flex flex-col gap-4 items-start justify-start p-[24px] relative w-full">
            <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[20px] text-left w_full">
              <p className="block leading-[28px]">Краткие итоги</p>
            </div>

            <div className="bg-[#ffffff] relative rounded-2xl shrink-0 w-full">
              <div className="flex flex-row items-center justify-center relative size-full">
                <div className="box-border content-stretch flex flex-row gap-2.5 items-center justify-center p-[24px] relative w_full">
                  <div className="basis-0 css-5cyu6a font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#000000] text-[14px] text-left tracking-[-0.084px]">
                    <p className="block leading-[20px]">
                      {interviewDetails?.overallSummary ||
                        "Детальная информация об интервью будет доступна после завершения собеседования."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Карточка 3: Детали */}
      <div className="bg-[#f5f6f1] relative rounded-[24px] shrink-0 w-full">
        <div className="relative size-full">
          <div className="box-border content-stretch flex flex-col gap-6 items-start justify-start p-[24px] relative w_full">
            <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[20px] text-left w_full">
              <p className="block leading-[28px]">Детали</p>
            </div>

            {interviewDetails?.answers?.map((answer: any, index: number) => (
              <div key={answer.id} className="bg-[#ffffff] relative rounded-2xl shrink-0 w_full">
                <div className="flex flex-row items-center justify-center relative size-full">
                  <div className="box-border content-stretch flex flex-col gap-4 items-start justify-start p-[24px] relative w_full">
                    <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w_full">
                      <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left">
                        <p className="block leading-[20px] text-[20px] text-[#000000]">Вопрос {index + 1}</p>
                      </div>
                      <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative min-w-0 text-[#000000] text-[20px] text-left">
                        <p className="block leading-[28px] whitespace-pre-line break-words break-all overflow-hidden">
                          {answer.questionText || "Текст вопроса не указан"}
                        </p>
                      </div>
                    </div>

                    {answer.feedback && (
                      <div className="box-border content-stretch flex flex-col gap-3 items-start justify-start relative w_full">
                        <div className="flex items-center gap-2 css-bbeeyw font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[12px] text-left">
                          <MessageCircleIcon className="w-4 h-4" />
                          <p className="block leading-[16px]">ИИ комментарий</p>
                        </div>
                        <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left">
                          <div className="block leading-[20px] whitespace-pre-line">{normalizeFeedback(answer.feedback)}</div>
                        </div>
                      </div>
                    )}

                    {expandedAnswers[index + 1] && (
                      <div className="bg-[#f8f9fa] relative rounded-[20px] shrink-0 w_full">
                        <div className="box-border content-stretch flex flex-col gap-3 items-start justify-start p-[16px] relative w_full">
                          <div className="css-bbeeyw font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[12px] text-left">
                            <p className="block leading-[16px]">Ответ кандидата</p>
                          </div>
                          <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left">
                            <div className="block leading-[20px] whitespace-pre-line">{answer.answerText || "Ответ не предоставлен"}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="box-border content-stretch flex flex-row items-center justify-between gap-4 p-0 relative shrink-0 w_full">
                      <div className={`css-bbeeyw font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[12px] text-left ${answer.score ? getScoreColor(answer.score) : ''}`}>
                        <p className="block leading-[16px]">Оценка: {answer.score ?? "—"}</p>
                      </div>
                      <button
                        onClick={() => onToggleAnswer(index + 1)}
                        className="css-bbeeyw font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[12px] text-left cursor-pointer transition-colors"
                        style={{ color: colors.primary }}
                      >
                        <p className="block leading-[16px]">{expandedAnswers[index + 1] ? "Скрыть ответ" : "Показать ответ"}</p>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Сообщение, если нет ответов */}
            {(!interviewDetails?.answers || interviewDetails.answers.length === 0) && (
              <div className="bg-[#ffffff] relative rounded-2xl shrink-0 w-full">
                <div className="flex flex-row items-center justify-center relative size-full">
                  <div className="box-border content-stretch flex flex-col gap-4 items-start justify-start p-[24px] relative w_full">
                    <div className="css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left">
                      <p className="block leading-[20px]">Ответы на вопросы пока не предоставлены или интервью еще не завершено.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


